/**
 * "Searching & filtering clients" tour.
 *
 * Quick walkthrough of the search box and four filter dropdowns on the
 * client list. Auto-targets the Clients page.
 *
 * Targets:
 *   data-tour="client-search"   — wrapper around the search input
 *   data-tour="client-filters"  — wrapper around the entire toolbar
 *
 * Field labels and search-match logic mirror ClientList.vue (filteredClients
 * computed around line 200-220) — keep in sync.
 */

import { NEXT, BACK, START, SKIP, DONE } from './_helpers.js';

function waitForElement(selector, timeout = 10000) {
    return new Promise((resolve) => {
        if (document.querySelector(selector)) return resolve();
        const start = Date.now();
        const check = () => {
            if (document.querySelector(selector)) return resolve();
            if (Date.now() - start > timeout) return resolve();
            setTimeout(check, 50);
        };
        check();
    });
}

export const searchFilterClientsTour = {
    id: 'search-filter-clients',
    title: 'Searching & filtering clients',
    description: 'How to find clients fast — the search box and the five filter dropdowns at the top of the client list.',
    autoLaunchOn: { page: 'clients' },

    steps: [
        {
            id: 'intro',
            title: 'Finding clients fast',
            text: `Once you have more than a handful of clients on file, the search box
                and filters at the top of the client list become essential. You can
                combine them — search and filter run together.`,
            buttons: [START],
        },
        {
            id: 'search',
            attachTo: { element: '[data-tour="client-search"]', on: 'bottom' },
            beforeShowPromise: () => waitForElement('[data-tour="client-search"]'),
            title: 'The search box',
            text: `Searches as you type. Matches against the client\'s
                <strong>name</strong>, <strong>ABN</strong>, or <strong>email</strong>
                — case-insensitive partial match. Useful when you remember just part of
                a name or an ABN.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'filters',
            attachTo: { element: '[data-tour="client-filters"]', on: 'bottom' },
            beforeShowPromise: () => waitForElement('[data-tour="client-filters"]'),
            title: 'The five filters',
            text: `Five dropdowns to narrow the list:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Risk Rating</strong> — High, Medium, or Low.</li>
                    <li><strong>CDD Status</strong> — Complete, In Progress, Overdue,
                        or Not Started. (See the <em>CDD &amp; client status meanings</em>
                        tour if you\'re unsure what each means.)</li>
                    <li><strong>Type</strong> — Individual, Body Corporate, Trust, or
                        Partnership.</li>
                    <li><strong>Staff</strong> — show only clients assigned to a
                        particular staff member.</li>
                    <li><strong>Review Status</strong> — narrow to clients whose
                        periodic review has been triggered, is overdue, or falls
                        due in the next 30 days.</li>
                </ul>
                Set "All …" on any filter to remove that constraint.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'combining',
            title: 'Combining search and filters',
            text: `Search and all five filters work <em>together</em>: the list shows
                clients that match the search string <strong>AND</strong> all active
                filters. Useful patterns:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Find your overdue reviews:</strong> set CDD = Overdue +
                        Staff = (you).</li>
                    <li><strong>Find every High-risk Trust:</strong> set Risk = High +
                        Type = Trust.</li>
                    <li><strong>Quickly locate one client:</strong> just type into the
                        search box, no filters needed.</li>
                </ul>`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'finish',
            title: 'Dashboard tile shortcuts',
            text: `Most metric tiles on the dashboard (e.g. "High risk", "Reviews
                overdue", "PEP clients") are clickable — clicking them brings you to
                the client list with the relevant filter already applied. Faster than
                setting filters by hand for common views.`,
            buttons: [BACK, DONE],
        },
    ],
};
